import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Share2, Copy, Check, Trophy, IndianRupee, Users, Sparkles } from "lucide-react";

const REWARD_INR = 500;

export default function Rewards() {
  const [profile, setProfile] = useState(null);
  const [board, setBoard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [me, lb] = await Promise.all([
        api.get("/referrals/me"),
        api.get("/referrals/leaderboard"),
      ]);
      setProfile(me.data);
      setBoard(lb.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load rewards");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const shareUrl = profile ? `${window.location.origin}/?ref=${profile.code}` : "";
  const shareText = `I'm using myinsuranceclaim.in for my motor insurance claims — they're independent and shockingly fast. Use my code ${profile?.code} to earn ₹${REWARD_INR} on your first claim.`;

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Link copied");
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "myinsuranceclaim.in", text: shareText, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const shareWhatsApp = () => {
    const t = encodeURIComponent(`${shareText} ${shareUrl}`);
    window.open(`https://wa.me/?text=${t}`, "_blank");
  };

  const apply = async (e) => {
    e.preventDefault();
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const r = await api.post("/referrals/apply", { code: applyCode.trim().toUpperCase() });
      toast.success(r.data.message || "Code applied");
      setApplyCode("");
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Could not apply code");
    } finally {
      setApplying(false);
    }
  };

  if (loading || !profile) return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] mesh-subtle py-10" data-testid="rewards-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] dark:text-blue-400 mb-2 flex items-center gap-2">
            <Gift className="w-3 h-3" /> Referral Rewards
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Give ₹{REWARD_INR}. Get ₹{REWARD_INR}.
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
            Share your code. When a friend files their first motor claim through us, you both earn ₹{REWARD_INR} cashback — credited instantly.
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat testid="rw-stat-earned" icon={IndianRupee} label="Earned so far" value={`₹${profile.rewards_earned_inr.toLocaleString("en-IN")}`} tone="from-[#00A86B] to-emerald-700" />
          <Stat testid="rw-stat-completed" icon={Trophy} label="Successful referrals" value={profile.referrals_completed} tone="from-[#FF7A00] to-orange-700" />
          <Stat testid="rw-stat-pending" icon={Users} label="Invited (pending)" value={profile.pending_referrals} tone="from-[#003DA5] to-blue-900" />
          <Stat testid="rw-stat-perreferral" icon={Sparkles} label="Per referral" value={`₹${REWARD_INR}`} tone="from-fuchsia-500 to-fuchsia-700" />
        </div>

        {/* Share card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-gradient-to-br from-[#003DA5] via-[#0055D4] to-[#003DA5] text-white p-8 relative overflow-hidden mb-6"
          data-testid="rw-share-card"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF7A00]/40 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#00A86B]/40 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Your unique code</div>
            <div className="font-mono font-black text-4xl sm:text-5xl tracking-tighter mt-1" data-testid="rw-code">{profile.code}</div>
            <div className="text-sm opacity-90 mt-2">Share this link — it auto-applies your code.</div>

            <div className="mt-5 flex items-center gap-2 bg-white/15 backdrop-blur rounded-xl px-3 py-2">
              <span className="flex-1 truncate font-mono text-xs sm:text-sm" data-testid="rw-share-url">{shareUrl}</span>
              <button onClick={copy} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="rw-copy" aria-label="Copy link">
                {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={share} className="bg-white text-[#003DA5] font-bold rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-white/90" data-testid="rw-share">
                <Share2 className="w-4 h-4" /> Share
              </button>
              <button onClick={shareWhatsApp} className="bg-[#25D366] text-white font-bold rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 hover:opacity-90" data-testid="rw-whatsapp">
                WhatsApp
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Apply someone else's code */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <h3 className="font-heading font-bold text-slate-900 dark:text-white mb-1">Have a code?</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Enter a friend&apos;s code to earn ₹{REWARD_INR} on your first claim.</p>
            {profile.referred_by ? (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-4 text-sm text-emerald-800 dark:text-emerald-300" data-testid="rw-applied">
                <Check className="w-4 h-4 inline mr-1" /> You&apos;re using code <span className="font-mono font-bold">{profile.referred_by}</span>
              </div>
            ) : (
              <form onSubmit={apply} className="flex gap-2" data-testid="rw-apply-form">
                <input
                  value={applyCode}
                  onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-3 py-2 font-mono focus:ring-2 focus:ring-[#003DA5] outline-none"
                  data-testid="rw-apply-input"
                />
                <button type="submit" disabled={applying} className="btn-signature rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid="rw-apply-btn">
                  {applying ? "Applying…" : "Apply"}
                </button>
              </form>
            )}
          </div>

          {/* Leaderboard */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <h3 className="font-heading font-bold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#FF7A00]" /> Top referrers</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Compete to climb the leaderboard.</p>
            {board.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6" data-testid="rw-lb-empty">Be the first! Share your code and top this list.</div>
            ) : (
              <ul className="space-y-2" data-testid="rw-leaderboard">
                {board.map((row, i) => (
                  <li key={row.email} className="flex items-center gap-3 rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${i === 0 ? "bg-[#FFB81C]" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-[#C77500]" : "bg-slate-300 text-slate-700"}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">{row.name || row.email.split("@")[0]}</div>
                      <div className="text-[10px] text-slate-500">{row.referrals_completed} referrals</div>
                    </div>
                    <div className="text-sm font-bold text-[#00A86B]">₹{row.rewards_earned_inr.toLocaleString("en-IN")}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* History */}
        {profile.history?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <h3 className="font-heading font-bold text-slate-900 dark:text-white mb-3">Activity</h3>
            <ul className="space-y-2 text-sm" data-testid="rw-history">
              {profile.history.slice().reverse().slice(0, 20).map((h, i) => (
                <li key={i} className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                  <span className="text-slate-700 dark:text-slate-300">
                    {h.kind === "invited" && `Invited ${h.referee_email}`}
                    {h.kind === "referee_reward" && `Earned ₹${h.reward_inr} for filing your first claim`}
                    {h.kind === "referrer_reward" && `Earned ₹${h.reward_inr} — a friend filed their first claim`}
                  </span>
                  <span className="text-[10px] text-slate-500">{new Date(h.at).toLocaleDateString("en-IN")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="card-premium p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tone} flex items-center justify-center mb-3 shadow-md`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      <div className="font-heading font-extrabold text-2xl text-slate-900 dark:text-white mt-0.5">{value}</div>
    </div>
  );
}
